import React, { useCallback, useMemo, useState } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { auth } from '../../firebase_utils/firebase';
import { createOrUpdateUser } from '../../firebase_utils/firebaseUtils';
import { X, Sparkles, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MagicLoader from '../common/MagicLoader';

const LoginModal = ({ isOpen, onClose, loginReason, onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  const handleSocialLogin = useCallback(async (provider) => {
    setIsLoading(true);
    setError(null);
    try {
      let authProvider;
      switch (provider) {
        case 'google':
          const { GoogleAuthProvider } = await import('firebase/auth');
          authProvider = new GoogleAuthProvider();
          break;
        // case 'apple':
        //   const { OAuthProvider } = await import('firebase/auth');
        //   authProvider = new OAuthProvider('apple.com');
        //   break;
        // case 'sso':
        //   setError('SSO login is not implemented yet.');
        //   setIsLoading(false);
        //   return;
        default:
          throw new Error('Unsupported provider');
      }

      const userCredential = await signInWithPopup(auth, authProvider);
      // Create/update user document with tier info
      await createOrUpdateUser(userCredential.user);
      
      onClose();
      navigate('/app');
      
      if (onLoginSuccess) {
        onLoginSuccess(userCredential.user);
      }
    } catch (error) {
      // console.error('Login error:', error);
      setError('Login failed. Please try again.');
    }
    setIsLoading(false);
  }, [onClose, onLoginSuccess, navigate]);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = useCallback((e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  }, []);

  const handleEmailLogin = useCallback(() => {
    if (!email) {
      setEmailError('Email is required');
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
      setError('Email login is not implemented yet.');
    }
  }, [email]);

  const getLoginMessage = () => {
    switch (loginReason) {
      case 'pro':
        return 'Please sign in to access Pro features';
      case 'attach':
        return 'Please sign in to upload files';
      default:
        return 'Please sign in to continue';
    }
  };

  const modalContent = useMemo(() => (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative font-kidBody border-4 border-[#E5FB5F] overflow-hidden"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#FFD54F]/20 to-[#FF6B6B]/20 rounded-bl-full -z-0"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#4F86F7]/20 to-[#5E60CE]/20 rounded-tr-full -z-0"></div>
      
      {/* Floating stars */}
      <motion.div 
        animate={{ y: [0, -10, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-6 left-6 text-yellow-400"
      >
        <Star className="w-6 h-6 fill-current" />
      </motion.div>
      <motion.div 
        animate={{ y: [0, -15, 0], rotate: [0, -10, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute bottom-12 right-6 text-[#FF6B6B]"
      >
        <Sparkles className="w-5 h-5" />
      </motion.div>

      <button 
        onClick={onClose}
        className="absolute right-4 top-4 text-gray-400 hover:text-[#FF6B6B] hover:bg-red-50 p-1 rounded-full transition-all z-10"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="text-center mb-8 relative z-10">
        <div className="inline-block p-3 bg-gradient-to-br from-[#E5FB5F] to-[#FFD54F] rounded-full mb-4 shadow-md">
          <img src="/images/logo/kuchipuchi_logo1.png" alt="Logo" className="w-12 h-12 object-contain" />
        </div>
        <h2 className="text-3xl font-bold text-[#5E60CE] font-kidHeading">Welcome!</h2>
        <p className="text-gray-600 mt-2 font-kidBody font-medium">{getLoginMessage()}</p>
      </div>

      {error && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl font-kidBody border border-red-100 flex items-center gap-2" 
          role="alert"
        >
          <div className="w-1 h-8 bg-red-400 rounded-full"></div>
          {error}
        </motion.div>
      )}

      <div className="space-y-4 relative z-10">
        <motion.button 
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleSocialLogin('google')}
          className="w-full py-3.5 px-4 border-2 border-[#E6EFFD] rounded-xl
                     text-[#5E60CE] font-bold hover:bg-[#F0F7FF] hover:border-[#5E60CE]/30 transition-all
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-3 font-kidBody shadow-sm bg-white"
          disabled={isLoading}
        >
          {isLoading ? (
            <MagicLoader size="small" message="" className="my-0" />
          ) : (
            <>
              <img src="/images/google-icon.png" alt="Google" className="w-6 h-6" />
              Continue with Google
            </>
          )}
        </motion.button>

        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t-2 border-[#E6EFFD]"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-400 font-bold font-kidBody text-xs uppercase tracking-wider">or</span>
          </div>
        </div>

        <div className="space-y-2">
          <input 
            type="email" 
            placeholder="Your email address" 
            value={email}
            onChange={handleEmailChange}
            className={`w-full px-5 py-3.5 border-2 rounded-xl font-kidBody bg-gray-50
                       focus:ring-4 focus:ring-[#5E60CE]/20 focus:border-[#5E60CE] focus:bg-white transition-all outline-none
                       placeholder-gray-400
                       ${emailError ? 'border-red-500 bg-red-50' : 'border-[#E6EFFD]'}`}
            aria-invalid={!!emailError}
            aria-label="Email"
          />
          {emailError && (
            <motion.p 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-sm text-red-500 font-bold font-kidBody ml-2" 
              role="alert"
            >
              {emailError}
            </motion.p>
          )}
        </div>
        
        <motion.button 
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleEmailLogin} 
          className="w-full py-3.5 px-4 bg-gradient-to-r from-[#5E60CE] to-[#4F86F7] 
                     text-white rounded-xl font-bold hover:shadow-lg hover:shadow-[#5E60CE]/30 transition-all
                     disabled:opacity-70 disabled:cursor-not-allowed font-kidBody
                     shadow-md flex items-center justify-center gap-2"
          disabled={isLoading || !!emailError}
        >
          {isLoading ? (
             <MagicLoader size="small" message="" className="my-0" />
          ) : (
            <>
              <span>Continue with Email</span>
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
              </div>
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  ), [loginReason, handleSocialLogin, handleEmailLogin, onClose, isLoading, error, email, emailError, handleEmailChange, getLoginMessage]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
        >
          {modalContent}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoginModal;